import { useEffect, useState } from 'react';

import { Flex, Text, useDisclosure, useToast, Menu, MenuButton, MenuList, MenuItem, IconButton, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, useDisclosure as useModalDisclosure, Box, Center } from '@chakra-ui/react';
import Cookies from 'js-cookie';
import { useLocation, useNavigate } from 'react-router-dom';

import LogOutIcon from '@/assets/LogOutIcon';
import { FiUser } from 'react-icons/fi';
import { FiDownload, FiPhone, FiSettings } from 'react-icons/fi';
import { ACTIVE_MENU, DEFAULT_COMPANY_NAME, DURATION_TOAST, ROLES } from '@/common/constants';
import { setAppId, setCompanyName } from '@/redux/slices/accountSlice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { RootState } from '@/redux/store';
import { extractCurrentPath } from '@/utils/letterPath';
import ModalLogOut from '../Modals/ModalLogOut';
import { HeaderProps } from './types';
import styles from './styles/Header.module.css';

const invoiceCreationPaths = [
    'Manage Invoices Inspector Add',
    'Manage Invoices Ship Owner Add'
];

const isInvoiceCreationPath = (path: string) => {
    return invoiceCreationPaths.includes(path.trim());
};

const Header: React.FC<HeaderProps> = ({ handleSetIsAuthenticated, isShowMenu = true }) => {
    const location = useLocation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const toast = useToast();
    const role = useAppSelector((state: RootState) => state.account.roleName);
    const companyName = useAppSelector((state: RootState) => state.account.companyName) || DEFAULT_COMPANY_NAME;
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [activeMenu, setActiveMenu] = useState('');
    const [username, setUsername] = useState<string>('');
    const contactModal = useModalDisclosure();
    const versionModal = useModalDisclosure();

    const handleToProfile = () => {
        if (!isShowMenu) {
            return;
        }

        if (role === 'super admin' || role === 'inspecting company') {
            navigate('/settings/profile');
        } else {
            navigate('/profile');
        }
    };

    const handleConfirmLogOut = async () => {
        try {
            clearState();
            navigate('/logout');
        } catch (error: unknown) {
            const errorMessage = (error instanceof Error) ? error.message : 'An error occurred while logging you out. Please try again.';

            toast({
                title: 'Logout Failed',
                description: errorMessage,
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    };

    const clearState = () => {
        handleSetIsAuthenticated(false);
        dispatch(setAppId(''));
        dispatch(setCompanyName(''));
        Cookies.remove('access_token');
        Cookies.remove('refresh_token');
        Cookies.remove('profile');
        Cookies.remove('expired_time');
    };

    useEffect(() => {
        const profile = Cookies.get('profile');
        if (profile) {
            const raw = JSON.parse(profile);
            setUsername(raw?.name || '-');
        }
    }, []);

    useEffect(() => {
        const currentActiveMenu = extractCurrentPath(location);
        const isCreatingInvoice = isInvoiceCreationPath(currentActiveMenu);
        setActiveMenu(isCreatingInvoice ? ACTIVE_MENU.CREATE_INVOICE : currentActiveMenu);
    }, [location]);

    // Map role to PDF filename
    const getUserGuideFile = (role: string) => {
        switch (role?.toLowerCase()) {
            case 'super admin':
                return 'User Manual Admin.pdf';
            case 'inspecting company':
                return 'User Manual Inspecting Company.pdf';
            case 'ship operator':
                return 'User Manual Ship Operator.pdf';
            case 'admin finance':
                return 'User Manual Admin Finance.pdf';
            case 'requestor':
                return 'User Manual Requestor (SIRE Review).pdf';
            default:
                return 'User Manual Admin.pdf';
        }
    };

    const handleDownloadUserGuide = () => {
        const fileName = getUserGuideFile(role);
        const link = document.createElement('a');
        link.href = `/user-manual/${fileName}`;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Get env values
    const contactHtml = import.meta.env.VITE_APP_CONTACT_HTML || '';
    const appVersion = import.meta.env.VITE_APP_VERSION || '';

    return (
        <>
            <ModalLogOut title="Log Out" isOpen={isOpen} onClose={onClose} handleConfirmLogOut={handleConfirmLogOut} />
            <Flex as="header" className={styles.header} justifyContent="space-between" alignItems="center">
                <Text fontSize="xl" fontWeight="bold">{isShowMenu ? activeMenu : ''}</Text>
                <Flex alignItems="center" className={styles.avatarContainer} gap={2}>
                    <Flex direction="column" alignItems="flex-start" justifyContent="center" className={styles.usernameContainer} onClick={handleToProfile} style={{ cursor: 'pointer', minWidth: 120 }}>
                        <Text fontWeight="bold" fontSize="md" style={{ marginBottom: '-2px' }}>{username || '-'}</Text>
                        <Text fontSize="sm" color="gray.500">
                            {isShowMenu ? (role === ROLES.SUPER_ADMIN ? DEFAULT_COMPANY_NAME : companyName) : (role === 'inspecting company' ? 'Inspecting Company' : 'Ship Operator')}
                        </Text>
                    </Flex>
                    <Flex alignItems="center" height="40px" mx={2}>
                        <div style={{ borderLeft: '1px solid #e2e8f0', height: '70%', margin: '0 6px' }} />
                    </Flex>
                    <Menu>
                        <MenuButton
                            as={IconButton}
                            aria-label="Options"
                            icon={<FiUser />}
                            variant="outline"
                            size="lg"
                            style={{ marginRight: '8px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff', width: '44px', height: '44px', minWidth: '44px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        />
                        <MenuList>
                            <MenuItem icon={<FiDownload />} onClick={handleDownloadUserGuide}>Download User Guide</MenuItem>
                            <MenuItem icon={<FiPhone />} onClick={contactModal.onOpen}>Info Kontak</MenuItem>
                            <MenuItem icon={<FiSettings />} onClick={versionModal.onOpen}>Versi Applikasi</MenuItem>
                        </MenuList>
                    </Menu>
                    <Flex alignItems="center" height="40px" mx={2}>
                        <div style={{ borderLeft: '1px solid #e2e8f0', height: '70%', margin: '0 6px' }} />
                    </Flex>
                    <IconButton
                        aria-label="Logout"
                        icon={<LogOutIcon />}
                        variant="outline"
                        size="lg"
                        style={{ marginLeft: '8px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff', width: '44px', height: '44px', minWidth: '44px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        onClick={onOpen}
                    />
                </Flex>
            </Flex>
            {/* Info Kontak Modal */}
            <Modal isOpen={contactModal.isOpen} onClose={contactModal.onClose} isCentered>
                <ModalOverlay />
                <ModalContent minWidth="360px" bgGradient="linear(to-br, #f8fafc, #e0e7ff)">
                    <ModalHeader>
                        <Center>
                            <Box bg="#6366f1" borderRadius="full" p={2} mr={2} display="inline-block">
                                <FiPhone color="#fff" size={22} />
                            </Box>
                            <Text fontWeight="bold" fontSize="lg" color="#3730a3">Info Kontak</Text>
                        </Center>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Box p={2} borderRadius="md" bg="#fff" boxShadow="md" textAlign="center" mb={4}>
                            <Box mb={2}>
                                <span dangerouslySetInnerHTML={{ __html: contactHtml }} />
                            </Box>
                        </Box>
                    </ModalBody>
                </ModalContent>
            </Modal>
            {/* Versi Aplikasi Modal */}
            <Modal isOpen={versionModal.isOpen} onClose={versionModal.onClose} isCentered>
                <ModalOverlay />
                <ModalContent minWidth="340px" bgGradient="linear(to-br, #f8fafc, #e0e7ff)">
                    <ModalHeader>
                        <Center>
                            <Box bg="#10b981" borderRadius="full" p={2} mr={2} display="inline-block">
                                <FiSettings color="#fff" size={22} />
                            </Box>
                            <Text fontWeight="bold" fontSize="lg" color="#065f46">Versi Aplikasi</Text>
                        </Center>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Box p={2} borderRadius="md" bg="#fff" boxShadow="md" textAlign="center" mb={4}>
                            <Text fontWeight="bold" fontSize="2xl" color="#10b981">{appVersion}</Text>
                            <Text fontSize="sm" color="gray.500" mt={2}>Pastikan Anda menggunakan versi terbaru aplikasi untuk pengalaman terbaik.</Text>
                        </Box>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
};

export default Header;
